import { Injectable, inject } from '@angular/core';
import {
  Storage,
  ref,
  uploadBytes,
  deleteObject
} from '@angular/fire/storage';

import { LoggerService } from '@core/services/infra/logger.service';
import { ChatAttachmentRef } from '@core/services/ai/ai-assistant.service';

const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB
const MAX_FILE_COUNT = 3;

const ALLOWED_PREFIXES = ['image/', 'application/pdf', 'application/json', 'text/'];

/**
 * Lightweight upload helper for chat attachments. Files land in
 * `chatAttachments/{workspaceId}/{conversationId}/{rand}-{name}` so they
 * can be cleaned up alongside the conversation if it's deleted.
 *
 * No download URLs are exposed — the chat handler reads the storage
 * path server-side and resolves authenticated URLs as needed.
 */
@Injectable({ providedIn: 'root' })
export class ChatAttachmentService {
  private readonly storage = inject(Storage);
  private readonly logger = inject(LoggerService);

  readonly maxFiles = MAX_FILE_COUNT;
  readonly maxBytesPerFile = MAX_FILE_BYTES;

  /**
   * Validate the file list client-side BEFORE uploading. Returns an
   * array of error keys (empty when accepted).
   */
  validate(files: File[]): { accepted: File[]; errors: string[] } {
    const errors: string[] = [];
    if (files.length > MAX_FILE_COUNT) {
      errors.push(`Up to ${MAX_FILE_COUNT} files per message.`);
    }
    const accepted: File[] = [];
    for (const f of files.slice(0, MAX_FILE_COUNT)) {
      if (f.size > MAX_FILE_BYTES) {
        errors.push(`${f.name} is over 5 MB.`);
        continue;
      }
      const ok = ALLOWED_PREFIXES.some((p) => f.type.startsWith(p));
      if (!ok && f.type) {
        errors.push(`${f.name} (${f.type}) is not a supported file type.`);
        continue;
      }
      accepted.push(f);
    }
    return { accepted, errors };
  }

  async uploadAll(
    workspaceId: string,
    conversationId: string,
    files: File[]
  ): Promise<ChatAttachmentRef[]> {
    if (files.length === 0) return [];
    const out: ChatAttachmentRef[] = [];
    for (const file of files) {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `chatAttachments/${workspaceId}/${conversationId}/${id}-${safeName}`;
      try {
        const r = ref(this.storage, path);
        await uploadBytes(r, file, {
          contentType: file.type || 'application/octet-stream'
        });
        out.push({
          name: file.name,
          mimeType: file.type || 'application/octet-stream',
          storagePath: path,
          sizeBytes: file.size
        });
      } catch (err) {
        this.logger.error(
          'chat attachment upload failed',
          'ChatAttachmentService',
          { err: (err as Error).message, name: file.name }
        );
        throw err;
      }
    }
    return out;
  }

  /** Best-effort cleanup when the user removes a staged attachment. */
  async deletePath(path: string): Promise<void> {
    try {
      await deleteObject(ref(this.storage, path));
    } catch (err) {
      // Best-effort: a failure here leaks an orphaned storage object. Log so it
      // is at least diagnosable in dev (LoggerService stays quiet in prod).
      this.logger.warn('chat attachment cleanup failed', 'ChatAttachmentService', {
        err: (err as Error).message
      });
    }
  }
}
